import i18nConfig from '@/i18nConfig'

/**
 * Where the language switcher should send the visitor.
 *
 * The naive answer — swap the `/[locale]/` segment and keep the rest — breaks
 * on any page whose slug differs per language (feature 024). Switching from
 * `/ar/library/blogs/<arabic-slug>` to English produced
 * `/library/blogs/<arabic-slug>`, which does not exist in the `en` registry.
 *
 * Every detail page already renders the right answer in its own `<head>`:
 * `buildLocalizedAlternates` emits `<link rel="alternate" hreflang="en"
 * href="…/library/blogs/<english-slug>">`. So we prefer that link and keep the
 * segment swap only as a fallback — which means this fixes courses, blogs,
 * books and videos at once, plus anything added later that emits hreflang.
 */

/** Locale codes we are willing to interpolate into a CSS selector. */
const SAFE_LOCALE = /^[a-zA-Z0-9-]+$/

/** True when the default locale is served without a `/xx` prefix. */
const defaultIsBare = !i18nConfig.prefixDefault

/**
 * Swap the leading locale segment for `newLocale`. This is the original
 * behaviour, kept as the fallback for pages that emit no hreflang (static
 * pages, the soft-404, anything rendered before metadata resolves).
 */
export const swapLocaleSegment = (pathname: string, newLocale: string): string => {
  const segments = pathname.split('/')
  const bare = i18nConfig.locales.includes(segments[1]) ? `/${segments.slice(2).join('/')}` : pathname
  const normalizedBare = bare === '' ? '/' : bare

  return newLocale === i18nConfig.defaultLocale && defaultIsBare
    ? normalizedBare
    : `/${newLocale}${normalizedBare === '/' ? '' : normalizedBare}`
}

/**
 * Validate an hreflang `href` and reduce it to a same-origin path.
 *
 * Returns null — so the caller falls back to the segment swap — when the link
 * is absent, unparseable, points at another origin, or does not actually carry
 * the locale prefix we asked for. That last check is what stops a stale or
 * mis-generated alternate from silently sending the visitor to the wrong
 * language; the switcher is a navigation control, so a wrong answer here is
 * worse than the old, predictable one.
 */
export const alternatePathForLocale = (
  href: string | null | undefined,
  newLocale: string,
  origin?: string | null
): string | null => {
  if (!href) return null

  let url: URL
  try {
    // `origin` is the base for relative hrefs. In a browser `link.href` is
    // already absolute, but metadata can also be authored root-relative.
    url = new URL(href, origin ?? undefined)
  } catch {
    return null
  }

  // Never let a link element navigate the user off-site.
  if (origin && url.origin !== origin) return null

  const path = url.pathname
  if (!path.startsWith('/')) return null

  const first = path.split('/')[1] ?? ''
  const expectsPrefix = !(newLocale === i18nConfig.defaultLocale && defaultIsBare)

  if (expectsPrefix) {
    // e.g. `/ar` or `/ar/...` — anything else is not the locale we asked for.
    if (first !== newLocale) return null
  } else if (i18nConfig.locales.includes(first)) {
    // Default locale is served bare, so a locale prefix here is wrong.
    return null
  }

  return path
}

/** Blog detail pages: `/library/blogs/<slug>` under any locale prefix. */
const BLOG_DETAIL = /^\/library\/blogs\/[^/]+\/?$/

/** The path without its leading locale segment (`/ar/x` → `/x`). */
const stripLocale = (pathname: string): string => {
  const segments = pathname.split('/')
  return i18nConfig.locales.includes(segments[1]) ? `/${segments.slice(2).join('/')}` : pathname
}

/**
 * The switcher's target URL: the page's own hreflang alternate when it is
 * usable, otherwise the plain segment swap.
 *
 * One exception: a blog article that emits NO alternate for `newLocale` does
 * not exist in that language (single-language articles advertise only their
 * own). Swapping the prefix would land on a redirect back to where the reader
 * already is, so they go to that language's article list instead.
 */
export const buildLocaleSwitchTarget = ({
  currentPathname,
  newLocale,
  alternateHref,
  origin,
}: {
  currentPathname: string
  newLocale: string
  alternateHref?: string | null
  origin?: string | null
}): string => {
  const alternate = alternatePathForLocale(alternateHref, newLocale, origin)
  if (alternate) return alternate
  if (!alternateHref && BLOG_DETAIL.test(stripLocale(currentPathname))) {
    return swapLocaleSegment('/library/blogs', newLocale)
  }
  return swapLocaleSegment(currentPathname, newLocale)
}

/**
 * Read `<link rel="alternate" hreflang="<locale>">` from the current document.
 * Returns null outside the browser or when the page emits no such link.
 */
export const readAlternateHref = (newLocale: string): string | null => {
  if (typeof document === 'undefined') return null
  if (!SAFE_LOCALE.test(newLocale)) return null

  const link = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${newLocale}"]`)
  return link?.href ?? null
}
